import convertMoney from "./convertMoney";

export const insertZero = (num) => num.toString().padStart(2, '0');

export const parseDurationList = (durationList) => {
  return Array.isArray(durationList) ? durationList : JSON.parse(durationList);
};

// Calculate and maintain client-server time offset for synchronization
let timeOffset = 0;

export const updateTimeOffset = (serverTime) => {
  if (serverTime) {
    const serverTimestamp = new Date(serverTime).getTime();
    const clientTimestamp = Date.now();
    timeOffset = serverTimestamp - clientTimestamp;
  }
};

export const getSynchronizedTime = () => {
  return Date.now() + timeOffset;
};

export const getTimeInfo = () => {
  return {
    offset: timeOffset,
    clientTime: Date.now(),
    synchronizedTime: getSynchronizedTime(),
    offsetInSeconds: Math.round(timeOffset / 1000)
  };
};

export const calculateElapsedTime = (startTime, serverTime = null) => {
  let currentTime;
  
  if (serverTime) {
    // Validate server time format
    const serverTimestamp = new Date(serverTime).getTime();
    if (isNaN(serverTimestamp)) {
      console.warn('🚀~ Invalid server time format:', serverTime, 'falling back to client time');
      currentTime = Date.now();
    } else {
      updateTimeOffset(serverTime);
      currentTime = serverTimestamp;
    }
  } else {
    // Use synchronized client time based on previously calculated offset
    currentTime = getSynchronizedTime();
  }
  const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
  return elapsedSeconds;
};

export const formatTime = (seconds, format = 'mm:ss') => {
  // Validation: ensure seconds is a valid number
  if (typeof seconds !== 'number' || !isFinite(seconds) || seconds < 0) {
    console.warn('🚀~ Invalid seconds for formatTime:', seconds);
    return format === 'HH:mm:ss' ? '00:00:00' : '00:00';
  }

  // Ensure seconds is an integer to prevent floating point issues
  const cleanSeconds = Math.floor(seconds);

  if (format === 'HH:mm:ss') {
    const hours = Math.floor(cleanSeconds / 3600);
    const minutes = Math.floor((cleanSeconds % 3600) / 60);
    const secs = cleanSeconds % 60;
    return `${insertZero(hours)}:${insertZero(minutes)}:${insertZero(secs)}`;
  } else {
    const minutes = Math.floor(cleanSeconds / 60);
    const secs = cleanSeconds % 60;
    return `${insertZero(minutes)}:${insertZero(secs)}`;
  }
};

export const initializeRegEndTime = (durationList, regEndLevel) => {
  console.log('🚀~regEndLevel :', regEndLevel)
  let regEndSum = 0;
  let foundRegEndLevel = false;
  
  for (let elem of durationList) {
    // If we found the reg_end_level, include it and continue for breaks
    if (elem[0] === regEndLevel) {
      foundRegEndLevel = true;
      regEndSum += parseInt(elem[1]);
      continue;
    }
    
    // If we found reg_end_level and this is a break, include it and stop
    if (foundRegEndLevel && elem[0] === "break") {
      regEndSum += parseInt(elem[1]);
      break; // Stop after the first break after reg_end_level
    }
    
    // If we found reg_end_level and this is the next level (not a break), stop
    if (foundRegEndLevel && typeof elem[0] === 'number' && elem[0] > regEndLevel) {
      break; // Stop at the next level after reg_end_level
    }
    
    // If we haven't found reg_end_level yet, keep adding
    if (!foundRegEndLevel) {
      regEndSum += parseInt(elem[1]);
    }
  }
  
  return regEndSum * 60;
};

export const initializeNextBreakTime = (durationList, durationIndex = -1) => {
  let nextBreakSum = 0;
  let foundBreak = false;
  
  // Start from NEXT level after current and look for next break (consistent naming)
  for (let index = durationIndex + 1; index < durationList.length; index++) {
    if (durationList[index][0] === "break") {
      foundBreak = true;
      break;
    }
    nextBreakSum += parseInt(durationList[index][1]);
  }
  
  if (!foundBreak) {
    console.log('🚀~No break found in duration list');
    return 0; // No break found
  }
  
  return nextBreakSum * 60;
};

export const getLevelAfterBreak = (durationList, currentBreakIndex) => {
  // Find the first non-break level after the current break
  for (let i = currentBreakIndex + 1; i < durationList.length; i++) {
    if (durationList[i][0] !== "break") {
      return durationList[i];
    }
  }
  return null; // No level after break found
};

export const processLevelUp = (gameState, durationList, elapsedSeconds) => {
  let isLevelUp = false;
  // Check if we need to level up (first time or time expired)
  if (gameState.durationIndex < 0 || gameState.totalMinute - elapsedSeconds < 0) {
    console.log('🚀~ Level up condition met!');
    
    // Handle multiple level ups if reconnecting after long time
    while (gameState.durationIndex < 0 || gameState.totalMinute - elapsedSeconds < 0) {
      gameState.durationIndex++;
      console.log('🚀~ Advancing to level index:', gameState.durationIndex);
      
      const currentLevel = durationList[gameState.durationIndex];
      if (!currentLevel) {
        console.log('🚀~ No more levels available - staying on last level');
        // Revert to last valid level index to prevent showing level "1"
        gameState.durationIndex = durationList.length - 1;
        // Reset isLevelUp since we didn't actually advance to a new level
        isLevelUp = false;
        break;
      }

      const levelDurationMinutes = parseInt(currentLevel[1]);
      const levelDurationSeconds = levelDurationMinutes * 60;

      gameState.totalMinute += levelDurationSeconds;
      isLevelUp = true;
      // Safety check to prevent infinite loop
      if (gameState.durationIndex >= durationList.length) {
        console.log('🚀~ Reached end of duration list');
        break;
      }
    }
  }

  return isLevelUp;
};

export const checkGameStateFlags = (gameState, durationList, eventData) => {
  const currentLevel = durationList[gameState.durationIndex];
  if (!currentLevel) return;

  // Check for early end
  if (!gameState.isEarlyEnd && eventData.early_end_level) {
    if (currentLevel[0] >= eventData.early_end_level) {
      gameState.isEarlyEnd = true;
      console.log('🚀~ Early end triggered');
    }
  }

  // Check for registration end
  if (!gameState.isRegEnd && eventData.reg_end_level) {
    if (currentLevel[0] >= eventData.reg_end_level) {
      gameState.isRegEnd = true;
      console.log('🚀~ Registration end triggered');
    }
  }
};

export const calculateNextBreakTime = (gameState, durationList, elapsedSeconds) => {
  // Edge case: validate inputs
  if (!gameState || !durationList || !Array.isArray(durationList) || typeof elapsedSeconds !== 'number') {
    console.warn('🚀~ Invalid inputs for calculateNextBreakTime');
    if (gameState) gameState.nextBreakMinute = 0;
    return;
  }
  
  // Edge case: check if durationIndex is valid
  if (gameState.durationIndex < -1 || gameState.durationIndex >= durationList.length) {
    console.warn('🚀~ Invalid durationIndex:', gameState.durationIndex);
    gameState.nextBreakMinute = 0;
    return;
  }
  
  // Find the next break in the duration list
  let nextBreakIndex = -1;
  for (let i = gameState.durationIndex + 1; i < durationList.length; i++) {
    // Edge case: validate duration list entry structure
    if (!Array.isArray(durationList[i]) || durationList[i].length < 2) {
      console.warn('🚀~ Malformed duration list entry at index:', i);
      continue;
    }
    
    if (durationList[i][0] === "break") {
      nextBreakIndex = i;
      break;
    }
  }
  
  if (nextBreakIndex === -1) {
    // No break found
    gameState.nextBreakMinute = 0;
    return;
  }
  
  // Calculate time from NOW until the next break:
  // remaining time in current level + all full levels until the break
  const remainingCurrentLevelSeconds = Math.max(0, gameState.totalMinute - elapsedSeconds);
  let betweenLevelsSeconds = 0;
  
  for (let i = gameState.durationIndex + 1; i < nextBreakIndex; i++) {
    // Edge case: validate duration entry and parse safely
    if (!Array.isArray(durationList[i]) || durationList[i].length < 2) {
      console.warn('🚀~ Skipping malformed duration entry at index:', i);
      continue;
    }
    
    const durationMinutes = parseInt(durationList[i][1]);
    if (isNaN(durationMinutes) || durationMinutes < 0) {
      console.warn('🚀~ Invalid duration minutes at index:', i, durationList[i][1]);
      continue;
    }
    
    betweenLevelsSeconds += durationMinutes * 60;
  }
  
  const timeToNextBreakSeconds = remainingCurrentLevelSeconds + betweenLevelsSeconds;

  // Edge case: ensure we don't set negative or invalid times
  if (timeToNextBreakSeconds < 0) {
    console.warn('🚀~ Calculated negative break time, setting to 0');
    gameState.nextBreakMinute = elapsedSeconds; // Will result in 0 remaining time
  } else {
    // Store as an absolute moment (so remaining = nextBreakMinute - elapsedSeconds)
    gameState.nextBreakMinute = elapsedSeconds + timeToNextBreakSeconds;
  }
};

export const calculateRemainingTimes = (gameState, elapsedSeconds, durationList, regEndLevel) => {
  // Validation: check inputs
  if (!gameState || typeof elapsedSeconds !== 'number' || elapsedSeconds < 0) {
    console.warn('🚀~ Invalid inputs for calculateRemainingTimes');
    return {
      levelTime: 0,
      regEndTime: 0,
      nextBreakTime: 0
    };
  }

  // Validation: ensure gameState properties are numbers
  const totalMinute = typeof gameState.totalMinute === 'number' ? gameState.totalMinute : 0;
  const regEndMinute = typeof gameState.regEndMinute === 'number' ? gameState.regEndMinute : 0;
  const nextBreakMinute = typeof gameState.nextBreakMinute === 'number' ? gameState.nextBreakMinute : 0;

  const levelTime = totalMinute - elapsedSeconds;
  const nextBreakTime = nextBreakMinute - elapsedSeconds;

  // Calculate registration close time based on current level and countdown
  let regEndTime = 0;
  if (regEndLevel && durationList && gameState.durationIndex >= 0) {
    const currentLevel = durationList[gameState.durationIndex];
    const currentLevelNumber = parseInt(currentLevel[0]);
    
    if (currentLevelNumber <= regEndLevel) {
      // We're still before or at the registration close level
      // Calculate time from current position to end of reg close level
      let timeToRegClose = 0;
      
      // Add remaining time in current level
      timeToRegClose += levelTime;
      
      // Add time for all levels between current and reg close level, including breaks
      let foundRegEndLevel = false;
      for (let i = gameState.durationIndex + 1; i < durationList.length; i++) {
        const level = durationList[i];
        const levelType = level[0];
        const levelDuration = parseInt(level[1]) * 60; // Convert minutes to seconds
        
        if (levelType === "break") {
          // Include break time if we've found the reg end level
          if (foundRegEndLevel) {
            timeToRegClose += levelDuration;
            break; // Stop after the first break after reg end level
          } else {
            // Include break time if we haven't reached reg end level yet
            timeToRegClose += levelDuration;
          }
        } else {
          const levelNumber = parseInt(levelType);
          
          if (levelNumber <= regEndLevel) {
            timeToRegClose += levelDuration;
            
            // Mark that we've found the reg end level
            if (levelNumber === regEndLevel) {
              foundRegEndLevel = true;
            }
          } else {
            break; // We've reached or passed the reg close level
          }
        }
      }
      
      regEndTime = timeToRegClose;
    } else {
      // We're past the registration close level
      regEndTime = 0;
    }
  } else {
    // Fallback to original calculation
    regEndTime = regEndMinute - elapsedSeconds;
  }

  // Validation: ensure all remaining times are non-negative and finite
  return {
    levelTime: Math.max(0, isFinite(levelTime) ? levelTime : 0),
    regEndTime: Math.max(0, isFinite(regEndTime) ? regEndTime : 0),
    nextBreakTime: Math.max(0, isFinite(nextBreakTime) ? nextBreakTime : 0)
  };
};

export const formatGameData = (gameState, durationList, elapsedSeconds, eventData, isLevelUp, idx) => {
  const remainingTimes = calculateRemainingTimes(gameState, elapsedSeconds, durationList, eventData.reg_end_level);
  const currentLevel = durationList[gameState.durationIndex];
  const nextLevel = durationList[gameState.durationIndex + 1];
  const levelAfterBreak = getLevelAfterBreak(durationList, gameState.durationIndex);
  const avgStack = eventData.rest_player > 0 ? Math.floor(eventData.total_stack / eventData.rest_player) : 0;

  return {
    event_idx: idx,
    time: formatTime(remainingTimes.levelTime),
    total_time: formatTime(elapsedSeconds, 'HH:mm:ss'),
    reg_end_time: formatTime(remainingTimes.regEndTime, 'HH:mm:ss'),
    next_break_time: formatTime(remainingTimes.nextBreakTime, 'HH:mm:ss'),
    level: currentLevel ? currentLevel[0] : "1",
    is_level_up: isLevelUp,
    sb: currentLevel ? currentLevel[2] : 0,
    bb: currentLevel ? currentLevel[3] : 0,
    ante: currentLevel ? currentLevel[4] : 0,
    next_level: nextLevel ? nextLevel[0] : null,
    next_sb: nextLevel ? nextLevel[2] : 0,
    next_bb: nextLevel ? nextLevel[3] : 0,
    next_ante: nextLevel ? nextLevel[4] : 0,
    level_after_break: levelAfterBreak ? levelAfterBreak[0] : null,
    level_after_break_sb: levelAfterBreak ? levelAfterBreak[2] : 0,
    level_after_break_bb: levelAfterBreak ? levelAfterBreak[3] : 0,
    level_after_break_ante: levelAfterBreak ? levelAfterBreak[4] : 0,
    rest_player: eventData.rest_player || 0,
    total_player: eventData.total_player || 0,
    re_entry: eventData.re_entry || 0,
    early_count: eventData.early_count || 0,
    total_stack: eventData.total_stack || 0,
    avg_stack: avgStack,
    pause: false,
    start_time: eventData.start_time,
    add_on: eventData.add_on || 0,
    event_state: eventData.event_state || "unknown", // Pass through event_state
  };
};

// OLD FORMULA avgStackInBB = totalStack / current bbStack 
// NEW FORMULA avgStackInBB = avg_stack / current bbStack
export const calculateBigBlind = (formattedData, parsedDurationList, gameState, timeData) => {
  const isBreak = timeData?.level === 'break'
  let bbStack = 0
  const currentDuration = parsedDurationList[gameState.durationIndex]
  const avgStack = Number(formattedData.avg_stack) || 0

  if(isBreak) {
    // Find the next non-break level
    let nextLevelIndex = gameState.durationIndex + 1
    while (nextLevelIndex < parsedDurationList.length && parsedDurationList[nextLevelIndex][0] === 'break') {
      nextLevelIndex++
    }
    
    if (nextLevelIndex < parsedDurationList.length) {
      bbStack = Number(parsedDurationList[nextLevelIndex][3]) || 0
    } else {
      bbStack = Number(timeData?.bb) || 0 // Fallback to current bb if no next level found
    }
  } else {
    bbStack = Number(currentDuration[3]) || 0
  }

  // Prevent division by zero which causes NaN
  if (bbStack === 0 || !isFinite(avgStack)) {
    return '0.0'
  }
  
  // Calculate how many big blinds
  const avgStackInBB = avgStack / bbStack 
  
  // Ensure the result is a valid number
  if (!isFinite(avgStackInBB)) {
    return '0.0'
  }
  
  return avgStackInBB.toFixed(1)
}

export const updateInfoData = (formattedData, setInfoData, parsedDurationList, gameState, timeData) => {

  setInfoData([
    {
      label: "Players / Entries",
      data: `${formattedData.rest_player} / ${formattedData.total_player}`,
    },
    {
      label: "Re-Entry / Early / Add-on",
      data: `${formattedData.re_entry} / ${formattedData.early_count} / ${formattedData.add_on}`,
    },
    {
      label: "Avg Stacks (BB)",
      data: `${formattedData.avg_stack ? convertMoney(formattedData.avg_stack) : "0"} (${calculateBigBlind(formattedData, parsedDurationList, gameState, timeData)})`,
    },
    {
      label: "Total Stacks",
      data: `${formattedData.total_stack ? convertMoney(formattedData.total_stack) : "0"}`,
    },
    {
      label: "Re-Entry",
      data: `${formattedData.re_entry}`,
    },
    {
      label: "Early",
      data: `${formattedData.early_count}`,
    },
    {
      label: "Add-on",
      data: `${formattedData.add_on}`,
    },
  ]);
};

export const resetGameState = (gameState, eventData) => {
  gameState.durationIndex = eventData.duration_index ?? -1;
  gameState.totalMinute = eventData.total_minute ?? 0;
  gameState.regEndMinute = eventData.reg_end_minute ?? 0;
  gameState.nextBreakMinute = eventData.next_break_minute ?? 0;
  gameState.isEarlyEnd = eventData.is_early_end ?? false;
  gameState.isRegEnd = eventData.is_reg_end ?? false;
};



export const getLevelProgressionFormattedData = (parsedDurationList,gameState,eventData,idx) => {
  
  // Use synchronized client time each tick; offset is updated on data events
  const elapsedSeconds = calculateElapsedTime(gameState.startTime, null);


  // Process level progression
  const isLevelUp = processLevelUp(gameState, parsedDurationList, elapsedSeconds);
  
  // Check game state flags
  checkGameStateFlags(gameState, parsedDurationList, eventData);
  
  // Calculate next break time
  calculateNextBreakTime(gameState, parsedDurationList, elapsedSeconds);

  // Format game data
  const formattedData = formatGameData(gameState, parsedDurationList, elapsedSeconds, eventData, isLevelUp, idx);

  return formattedData;
}

// Test utility for break time calculation accuracy
// Usage: Open browser console and run: window.testBreakTimeCalculation()
// Or import and call from React components for debugging
export const testBreakTimeCalculation = () => {
  console.log('🚀~ Testing break time calculation accuracy...');
  
  // Test scenario 1: Simple duration list with one break
  const testDurationList1 = [
    ["1", "10", "25", "50", "0"],
    ["2", "15", "50", "100", "0"], 
    ["break", "5", "0", "0", "0"],
    ["3", "20", "75", "150", "25"]
  ];
  
  const testGameState1 = {
    durationIndex: 0,
    totalMinute: 10 * 60, // 10 minutes for level 1
    nextBreakMinute: 0
  };
  
  // Should find break after level 2 (15 more minutes + current remaining)
  calculateNextBreakTime(testGameState1, testDurationList1, 5 * 60); // 5 minutes elapsed
  const expectedBreakTime1 = (5 * 60) + (5 * 60) + (15 * 60); // elapsed + remaining level 1 + level 2
  
  console.log('Test 1 - Expected break time:', expectedBreakTime1, 'Calculated:', testGameState1.nextBreakMinute);
  console.log('Test 1 Result:', testGameState1.nextBreakMinute === expectedBreakTime1 ? '✅ PASS' : '❌ FAIL');
  
  // Test scenario 2: No breaks in duration list
  const testDurationList2 = [
    ["1", "10", "25", "50", "0"],
    ["2", "15", "50", "100", "0"]
  ];
  
  const testGameState2 = {
    durationIndex: 0,
    totalMinute: 10 * 60,
    nextBreakMinute: 100 // Should be reset to 0
  };
  
  calculateNextBreakTime(testGameState2, testDurationList2, 5 * 60);
  console.log('Test 2 - Expected: 0, Calculated:', testGameState2.nextBreakMinute);
  console.log('Test 2 Result:', testGameState2.nextBreakMinute === 0 ? '✅ PASS' : '❌ FAIL');
  
  // Test scenario 3: Already at last level before break
  const testGameState3 = {
    durationIndex: 1, // At level 2
    totalMinute: (10 + 15) * 60, // Total time for levels 1 + 2
    nextBreakMinute: 0
  };
  
  calculateNextBreakTime(testGameState3, testDurationList1, (10 + 10) * 60); // 20 minutes elapsed (10 remaining in level 2)
  const expectedBreakTime3 = (20 * 60) + (5 * 60); // elapsed + remaining level 2
  
  console.log('Test 3 - Expected break time:', expectedBreakTime3, 'Calculated:', testGameState3.nextBreakMinute);  
  console.log('Test 3 Result:', testGameState3.nextBreakMinute === expectedBreakTime3 ? '✅ PASS' : '❌ FAIL');
  
  // Test remaining time calculation
  const remainingTimes = calculateRemainingTimes(testGameState3, (10 + 10) * 60);
  const expectedRemainingBreakTime = expectedBreakTime3 - (20 * 60);
  console.log('Test 4 - Remaining break time expected:', expectedRemainingBreakTime, 'Calculated:', remainingTimes.nextBreakTime);
  console.log('Test 4 Result:', remainingTimes.nextBreakTime === expectedRemainingBreakTime ? '✅ PASS' : '❌ FAIL');
  
  console.log('🚀~ Break time calculation tests completed');
};
